/**
 * datePickerValue — normalise whatever we hold in state into what
 * react-datepicker's `selected` prop can safely take: a real `Date`, or `null`.
 *
 * WHAT'S ACTUALLY WRONG (#403, react-datepicker 7 → 9) — measured against v9,
 * not assumed. The upgrade was expected to break on "our state holds strings,
 * v8+ demands Dates". **That premise is false**: v9 renders a *parseable* date
 * string in `selected` perfectly well ("8/13/2026" → 8/13/2026). The real
 * hazard is narrower and nastier.
 *
 * **v9 THROWS `Invalid time value` mid-render for an unparseable string or an
 * Invalid Date**, and our state reaches exactly that state on a normal user
 * action. Every one of our onChange handlers runs:
 *
 *     const fmDate = new Date(Date.parse(date)).toLocaleDateString("en-us");
 *
 * react-datepicker's signature is `onChange(date: Date | null)` — it passes
 * **null when the user clears the field**. `Date.parse(null)` is `NaN`, so that
 * line stores the literal string `"Invalid Date"`, which lands straight back in
 * `selected` on the next render and throws *while rendering*. A throw there is
 * not a bad value in a box; it unmounts the modal.
 *
 * So this helper turns anything unusable into `null` — an empty field, which is
 * visible and recoverable. (Whether v7 threw on the same path was not tested;
 * the helper closes it either way.)
 *
 * WHY NOT JUST HOLD A DATE IN STATE — the trap that makes this the right fix
 * rather than the obvious one:
 * `buildScheduleUpdateFields` decides whether to send a field to FileMaker with
 * `current.end !== original.end`, a **string** comparison against the original
 * job document. Switching the state to a Date makes that `Date !== string`,
 * which is ALWAYS true — so every save would push `date_estimatedCompletion`
 * downstream whether or not the user touched it. That is the phantom-update
 * class behind #73 / #80 / #83 / #84, and why `src/utility/dateCompare.js`
 * exists. Keeping the state a string leaves every comparison and mutation
 * payload byte-for-byte unchanged, and converts at the ONE boundary that needs
 * it: the `selected` prop.
 *
 * A Date passes through untouched, so it is safe to apply uniformly — including
 * SuppressModal, whose state genuinely holds a Date. Applying it there too
 * means a later edit that switches that state to a string cannot quietly
 * reintroduce the throw.
 */

/**
 * @param {Date|string|number|null|undefined} value
 * @returns {Date|null} a valid Date, or null when there is nothing safe to show
 */
export function toDatePickerValue(value) {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    // An Invalid Date is still `instanceof Date` — screen it out here or it
    // reaches date-fns and throws mid-render.
    return Number.isNaN(value.getTime()) ? null : value;
  }

  // Strings ("8/13/2026", ISO, and the "Invalid Date" our handlers can produce)
  // and epoch numbers go through the Date constructor. Booleans and objects
  // deliberately do not — `new Date(true)` is a VALID date (epoch + 1ms), so a
  // plain NaN check would pass a boolean through as 1970.
  if (typeof value !== "string" && typeof value !== "number") return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
